import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AuctionIntelligenceService from 'services/AuctionIntelligenceService';
import { UploadResponse } from '../types';

const ACCOUNTS = [
  { id: '6907204135', name: 'Dosaze (all geos)' },
  { id: '2012504968', name: 'CoCozy / Araluna' },
  { id: '9490408400', name: 'Noonlash' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
  service: AuctionIntelligenceService;
}

// The Google Ads API keeps auction-insight metrics behind a closed allowlist, so
// competitor share data enters via the UI's CSV export. Re-uploads are safe: rows
// merge on (account, entity, week, domain).
export default function UploadDialog({ open, onClose, onUploaded, service }: Props) {
  const [accountId, setAccountId] = useState(ACCOUNTS[0].id);
  const [fileName, setFileName] = useState<string | null>(null);
  const [csv, setCsv] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<UploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    setError(null);
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result ?? ''));
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!csv) return;
    setBusy(true);
    setError(null);
    const resp = await service.uploadCsv(csv, accountId);
    setBusy(false);
    if (!resp) {
      setError('Upload failed — check the file is an Auction insights export.');
      return;
    }
    setResult(resp);
    onUploaded();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Auction Insights CSV</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          In Google Ads: Insights &amp; reports → Auction insights (or Report
          Editor), segment by <b>week</b>, include <b>Campaign</b> / <b>Ad group</b>{' '}
          columns if possible, then Download → CSV. Re-uploading the same weeks
          updates existing rows.
        </Typography>
        <TextField
          select
          fullWidth
          size="small"
          label="Account"
          value={accountId}
          onChange={e => setAccountId(e.target.value)}
          sx={{ mb: 2 }}
        >
          {ACCOUNTS.map(a => (
            <MenuItem key={a.id} value={a.id}>
              {a.name}
            </MenuItem>
          ))}
        </TextField>
        <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
          Choose CSV
          <input hidden type="file" accept=".csv,text/csv" onChange={handleFile} />
        </Button>
        {fileName && (
          <Typography variant="body2" component="span" sx={{ ml: 1 }}>
            {fileName}
          </Typography>
        )}
        <Box sx={{ mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {result && (
            <Alert severity={result.warnings.length > 0 ? 'warning' : 'success'}>
              Ingested {result.inserted} rows
              {result.level ? ` (${result.level} level)` : ''}.
              {result.warnings.map(w => (
                <div key={w}>{w}</div>
              ))}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!csv || busy}
        >
          {busy ? 'Uploading…' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
